import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHelmet from "@/components/base/SEOHelmet";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories, videos } from "@/mocks/videos";

const categorySchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "91看网页版 - 分类浏览",
  description: "91看网页版提供丰富的影视分类浏览功能，包括动作片、喜剧片、科幻片、恐怖片等多种类型，方便用户快速找到想看的内容。",
  url: "https://www.91kan.com/category/all",
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const filteredVideos = useMemo(() => {
    let result = activeCategory === "all" ? videos : videos.filter((v) => v.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.tags.some((t) => t.toLowerCase().includes(q)) ||
          v.description.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  return (
    <>
      <ScrollToTop />
      <SEOHelmet
        title={`${currentCategory?.name || "全部"} - 91看网页版分类浏览`}
        description={`91看网页版${currentCategory?.name || "全部"}分类，海量高清视频内容，无需下载安装即可在线观看。91看网页版最佳在线影视平台。`}
        keywords={`91看网页版,91看网页版在线观看,${currentCategory?.name || "影视"},高清播放,在线影视`}
        canonicalUrl={`https://www.91kan.com/category/${activeCategory}`}
        schemaData={categorySchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Breadcrumb
              items={[
                { name: "首页", path: "/" },
                { name: "分类浏览" },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                {currentCategory?.name || "全部"}影视
              </h1>
              <p className="text-sm text-foreground-500">
                共 {filteredVideos.length} 部作品 · 91看网页版在线观看
              </p>
            </div>

            {/* Category Tabs */}
            <div className="mb-8 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      activeCategory === cat.slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {cat.icon && <i className={`${cat.icon} text-xs`}></i>}
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative max-w-md">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索影视名称或标签..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Video Grid */}
            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 pb-16" data-product-shop>
                {filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img
                        src={video.poster}
                        alt={video.title}
                        title={`${video.title} - 91看网页版在线观看`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/60 text-yellow-400 text-xs">
                        <i className="ri-star-fill text-[10px]"></i>
                        {video.rating}
                      </div>
                      {video.isVip && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-[10px] font-medium">
                          VIP
                        </div>
                      )}
                      {video.isNew && !video.isVip && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-[10px] font-medium">
                          NEW
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1">
                      {video.year} · {video.duration}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-search-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500 text-sm mb-2">没有找到相关内容</p>
                <p className="text-foreground-300 text-xs">尝试更换搜索关键词或浏览其他分类</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}